/**
 * CSS Variables Generator
 *
 * Gera CSS variables a partir do theme para uso em componentes.
 * Inspirado no sistema colors.php do Yii que gera CSS dinamicamente.
 *
 * @since 2025-12-16
 * @epic INIT-0045-03, INIT-0046
 */

import type { Theme, ButtonVariant, MenuStyle, BlockStyle } from './types';

// ==========================================
// TYPES
// ==========================================

export type CssVariables = Record<string, string>;

// ==========================================
// HELPERS
// ==========================================

/**
 * Converte uma cor hex para componentes HSL sem wrapper hsl()
 * Formato retornado: "H S% L%" (ex: "216 98% 52%")
 * Esse formato é o usado pelas CSS variables do shadcn/ui
 */
export function hexToHslValues(hex: string): string {
  if (!hex || hex === '') return '0 0% 0%';

  let cleanHex = hex.trim().replace('#', '');

  // Normaliza hex de 3 dígitos (ex: "fff" -> "ffffff")
  if (/^[0-9a-fA-F]{3}$/.test(cleanHex)) {
    cleanHex = cleanHex.split('').map((c) => c + c).join('');
  }

  // Entrada não é hex de 6 dígitos válido (nome de cor, rgb(), hex malformado
  // vindo do tema do tenant) — degrada para o fallback em vez de gerar NaN.
  if (!/^[0-9a-fA-F]{6}$/.test(cleanHex)) return '0 0% 0%';

  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    // Achromatic
    return `0 0% ${Math.round(l * 100)}%`;
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  if (max === r) {
    h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  } else if (max === g) {
    h = ((b - r) / d + 2) / 6;
  } else {
    h = ((r - g) / d + 4) / 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Escurece uma cor hex
 */
function darkenColor(hex: string, percent: number): string {
  if (!hex || hex === '') return hex;

  // Remove # se presente
  const cleanHex = hex.replace('#', '');

  // Parse RGB
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  // Escurecer
  const factor = 1 - percent / 100;
  const newR = Math.round(r * factor);
  const newG = Math.round(g * factor);
  const newB = Math.round(b * factor);

  // Converter de volta para hex
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

/**
 * Clareia uma cor hex
 */
function lightenColor(hex: string, percent: number): string {
  if (!hex || hex === '') return hex;

  const cleanHex = hex.replace('#', '');

  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  const factor = percent / 100;
  const newR = Math.round(r + (255 - r) * factor);
  const newG = Math.round(g + (255 - g) * factor);
  const newB = Math.round(b + (255 - b) * factor);

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

/**
 * Garante luminância mínima para uso em dark mode.
 * Converte hex → HSL, sobe L para minL se necessário, converte de volta.
 */
function ensureMinLuminance(hex: string, minL = 55): string {
  if (!hex || hex === '') return hex;
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  const targetL = Math.max(l * 100, minL) / 100;
  if (targetL === l) return hex;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let nr: number, ng: number, nb: number;
  if (s === 0) {
    nr = ng = nb = targetL;
  } else {
    const q = targetL < 0.5 ? targetL * (1 + s) : targetL + s - targetL * s;
    const p = 2 * targetL - q;
    nr = hue2rgb(p, q, h + 1 / 3);
    ng = hue2rgb(p, q, h);
    nb = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (n: number) => Math.round(n * 255).toString(16).padStart(2, '0');
  return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
}

/**
 * Inverte cores para dark mode
 */
function invertForDarkMode(hex: string): string {
  if (!hex || hex === '') return hex;

  // Se for branco ou muito claro, escurece
  // Se for escuro, clareia
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  if (luminance > 0.7) {
    // Cor clara -> escurecer para dark mode
    return darkenColor(hex, 70);
  } else if (luminance < 0.3) {
    // Cor escura -> clarear para dark mode
    return lightenColor(hex, 50);
  }

  return hex;
}

// ==========================================
// GENERATORS
// ==========================================

/**
 * Gera variáveis para menu sidebar (branding por tenant)
 */
function generateSidebarMenuVariables(
  prefix: string,
  menu: MenuStyle,
  isDarkMode: boolean
): CssVariables {
  return {
    [`--${prefix}`]: isDarkMode ? invertForDarkMode(menu.background) : menu.background,
    [`--${prefix}-foreground`]: isDarkMode ? invertForDarkMode(menu.text) : menu.text,
    [`--${prefix}-border`]: menu.border,
    [`--${prefix}-active`]: isDarkMode ? lightenColor(menu.activeBackground, 15) : menu.activeBackground,
    [`--${prefix}-active-foreground`]: isDarkMode ? lightenColor(menu.activeText, 30) : menu.activeText,
    [`--${prefix}-hover`]: isDarkMode ? lightenColor(menu.hoverBackground, 10) : menu.hoverBackground,
    [`--${prefix}-hover-foreground`]: isDarkMode ? lightenColor(menu.hoverText, 40) : menu.hoverText,
  };
}

// ==========================================
// MAIN GENERATOR
// ==========================================

// ==========================================
// DARK MODE PALETTE (Neutral, hue ZERO) [TMF9-54657]
//
// Migrada de gray-* / slate-* (hue 220 / 217 — AZULADOS) para neutral-* puro
// (hue 0, sat 0%). Diretriz: "dark = preto + opacidade" — sem matizes
// azuladas, brancas ou cinza azulado.
//
// História:
//   - TMF9-54310: migrou slate-* → gray-* achando que ficaria neutro.
//     Mas hue 220 ainda puxa azul perceptível.
//   - TMF9-54657: migra para neutral-* (hue 0). Resolve definitivamente.
//
// Esta palette é injetada inline em <html> pelo ThemeProvider em runtime
// e SOBRESCREVE os fallbacks estáticos de index.css.
// ==========================================
const DARK_PALETTE = {
  // Backgrounds (do mais escuro ao mais claro) — todos neutros (hue 0)
  // Diretriz: cards SOBEM em luminância sobre o bg pra criar elevação visível.
  // Preto puro sobre bg escuro fica indistinguível — perda de hierarquia.
  bg: '#171717',           // neutral-900 - fundo principal da página
  card: '#1a1a1a',         // sutilmente acima do bg p/ elevação (alinha com --card em index.css, v9.58.0 TMF9-54758)
  elevated: '#262626',     // neutral-800 - elementos elevados, hovers
  surface: '#1f1f1f',      // cinza neutro - superfícies gerais

  // Borders (cinzas neutros perceptíveis)
  border: '#333333',       // cinza neutro - bordas padrão
  borderLight: '#525252',  // neutral-600 - bordas destacadas

  // Text (off-white puramente neutros, sem hue)
  text: '#f5f5f5',         // neutral-100 - texto principal
  textSecondary: '#a3a3a3', // neutral-400 - texto secundário
  textMuted: '#737373',    // neutral-500 - texto sutil/placeholder

  // Interactive states
  hover: '#262626',        // neutral-800 - hover state
  active: '#404040',       // neutral-700 - active/pressed state
  focus: '#3b82f6',        // blue-500 - focus ring (intencionalmente azul)

  // Navigation
  sidebar: '#171717',      // neutral-900 - sidebar background
  topbar: '#1f1f1f',       // cinza neutro - topbar background

  // Inputs
  // [TMF9-55436] inputBorder elevado para neutral-500 (#737373) atingir 4.74:1
  // contra fundo branco do input (forçado por !important em index.css L281), bem
  // acima do mínimo WCAG AA 3:1 para componentes de UI.
  input: '#1f1f1f',        // cinza neutro - input background
  inputBorder: '#737373',  // neutral-500 - input border (WCAG AA 4.74:1 sobre #fff)

  // Tables
  tableHeader: '#1f1f1f',  // cinza neutro - table header
  tableRowHover: '#262626', // neutral-800 - row hover
  tableBorder: '#333333',  // cinza neutro - table borders

  // Shadows (mais fortes para dark mode)
  shadowSm: '0 1px 2px 0 rgba(0, 0, 0, 0.4)',
  shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.5), 0 1px 2px -1px rgba(0, 0, 0, 0.5)',
  shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.6), 0 4px 6px -4px rgba(0, 0, 0, 0.6)',
};

// Light mode palette [TMF9-53595]
// bg: gray-50 em vez de gray-100 — light mode mais sutil, menos brilho.
// Cards (card/elevated/surface) permanecem brancos para criar contraste com o bg.
const LIGHT_PALETTE = {
  bg: '#f9fafb',           // gray-50 — fundo base do app (mais sutil que gray-100)
  card: '#ffffff',         // white — cards destacam sobre o bg gray-50
  elevated: '#ffffff',     // white
  surface: '#ffffff',      // white

  border: '#e5e7eb',       // gray-200
  borderLight: '#d1d5db',  // gray-300

  text: '#1f2937',         // gray-800
  textSecondary: '#4b5563', // gray-600
  textMuted: '#9ca3af',    // gray-400

  hover: '#f3f4f6',        // gray-100 — hover sobre cards/superfícies
  active: '#e5e7eb',       // gray-200
  focus: '#3b82f6',        // blue-500

  // Inputs
  // [TMF9-55436] inputBorder elevado de gray-300 (#d1d5db, 1.47:1 sobre branco)
  // para gray-500 (#6b7280, 4.83:1) — atende WCAG AA 3:1 para componentes de UI.
  // Fundo de input permanece branco (consistente com regra global em index.css).
  input: '#ffffff',        // white - input background
  inputBorder: '#6b7280',  // gray-500 - input border (WCAG AA 4.83:1 sobre #fff)

  tableHeader: '#f9fafb',  // gray-50
  tableRowHover: '#f3f4f6', // gray-100
  tableBorder: '#e5e7eb',  // gray-200

  shadowSm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
  shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
};

/**
 * Gera todas as CSS variables a partir do theme
 * IMPORTANTE: Cores light são salvas como base, dark mode é calculado automaticamente!
 *
 * Estratégia Dark Mode:
 * - Backgrounds: escurecem 80% (ficam escuros)
 * - Textos e links: clareiam 50% (ficam claros para contraste)
 * - Sidebar/Topbar: escurecem 30% (mantém identidade visual)
 */
export function generateCssVariables(theme: Theme, isDarkMode: boolean = false): CssVariables {
  const vars: CssVariables = {};
  const palette = isDarkMode ? DARK_PALETTE : LIGHT_PALETTE;

  // ==========================================
  // SHADCN/UI CORE TOKENS (fonte única de verdade)
  // Formato HSL sem wrapper: "H S% L%"
  // ==========================================
  const primaryBg = theme.buttons.primary?.background || theme.colors.link || '#0074ba';
  const primaryText = theme.buttons.primary?.text || '#ffffff';
  const dangerBg = theme.buttons.danger.background || '#dc3545';
  const dangerText = theme.buttons.danger.text || '#ffffff';

  // Primary — cor primária do tema do tenant
  vars['--primary'] = hexToHslValues(isDarkMode ? lightenColor(primaryBg, 20) : primaryBg);
  vars['--primary-foreground'] = hexToHslValues(primaryText);

  // Background e foreground
  // [TMF9-54675] Em dark mode, só usar a cor do tenant se ele explicitamente definiu
  // background.colorDark. Sem isso, ThemeContext.resolvedTheme propaga a cor light
  // (background.color) para .color, vazando a identidade light do tenant (ex: Copa
  // verde) sobre o fundo dark — usar palette.bg neutro nesse caso.
  const tenantBg = isDarkMode
    ? (theme.background.colorDark || palette.bg)
    : (theme.background.color || palette.bg);
  vars['--background'] = hexToHslValues(tenantBg);
  vars['--foreground'] = hexToHslValues(palette.text);

  // Superfícies do dark DERIVADAS do fundo do tenant (TMF9-63760).
  // Antes, --card/--popover/--muted/--accent/--secondary recebiam a cor fixa
  // DARK_PALETTE.card (#1a1a1a, neutra e opaca). Em tenant com
  // background.colorDark colorido (ex.: demo navy #1a283c) isso virava um bloco
  // preto sem integração com o fundo — tabelas e cards "pretos sem transparência".
  // Clarear o próprio fundo do tenant em poucos % mantém a elevação visível
  // (diretriz da DARK_PALETTE: card SOBE em luminância) e a harmonia com a cor
  // do tenant; e, por ser opaco, continua compatível com modificadores de
  // opacidade (`bg-card/50`, `bg-muted/40`), o que um valor com alpha quebraria.
  const darkSurface = (percent: number) => lightenColor(tenantBg, percent);

  // Card — superfícies de cards/containers
  vars['--card'] = hexToHslValues(isDarkMode ? darkSurface(3) : palette.card);
  vars['--card-foreground'] = hexToHslValues(palette.text);

  // Popover — um degrau acima do card (menus/dropdowns sobre cards)
  vars['--popover'] = hexToHslValues(isDarkMode ? darkSurface(6) : palette.card);
  vars['--popover-foreground'] = hexToHslValues(palette.text);

  // Secondary
  vars['--secondary'] = hexToHslValues(isDarkMode ? darkSurface(5) : '#e9ecef');
  vars['--secondary-foreground'] = hexToHslValues(isDarkMode ? palette.text : '#1f2937');

  // Muted — cabeçalho de tabela (`bg-muted/50`), fundos discretos
  vars['--muted'] = hexToHslValues(isDarkMode ? darkSurface(5) : '#f1f5f9');
  vars['--muted-foreground'] = hexToHslValues(palette.textMuted);

  // Accent — hover de linha (`hover:bg-muted/50`, `hsl(var(--accent))`)
  vars['--accent'] = hexToHslValues(isDarkMode ? darkSurface(8) : '#f1f5f9');
  vars['--accent-foreground'] = hexToHslValues(palette.text);

  // Destructive
  vars['--destructive'] = hexToHslValues(isDarkMode ? lightenColor(dangerBg, 15) : dangerBg);
  vars['--destructive-foreground'] = hexToHslValues(dangerText);

  // Border, input, ring
  // [TMF9-55436] --input agora usa inputBorder em ambos os modos (light e dark)
  // para alcançar contraste WCAG AA 3:1. Antes, light mode usava palette.border
  // (gray-200) que tinha apenas 1.24:1 sobre fundo branco do input.
  // Borda no dark também derivada do fundo do tenant (TMF9-63760): cinza fixo
  // (#333333) sobre fundo colorido parecia "sujo"; ~11% acima do fundo mantém
  // a separação perceptível sem introduzir um segundo matiz.
  vars['--border'] = hexToHslValues(isDarkMode ? darkSurface(11) : palette.border);
  vars['--input'] = hexToHslValues(palette.inputBorder);
  vars['--ring'] = hexToHslValues(isDarkMode ? lightenColor(primaryBg, 20) : primaryBg);

  // ==========================================
  // BRANDING TOKENS (customização por tenant)
  // Formato: valor direto (hex/url), NÃO HSL
  // ==========================================

  // Sidebar & Topbar
  const sidebarBg = theme.colors.sidebarBackground || '#2a4d62';
  const topbarBg = theme.colors.topbarBackground || '#2a4d62';
  const topbarText = theme.colors.topbarText || '#ffffff';
  const topbarBorder = theme.colors.topbarBorder || '#1e3a4f';

  vars['--sidebar'] = isDarkMode
    ? (theme.colors.sidebarBackgroundDark || darkenColor(sidebarBg, 35))
    : sidebarBg;
  vars['--topbar'] = isDarkMode
    ? (theme.colors.topbarBackgroundDark || darkenColor(topbarBg, 35))
    : topbarBg;
  vars['--topbar-foreground'] = isDarkMode
    ? (theme.colors.topbarTextDark || topbarText)
    : topbarText;
  vars['--topbar-border'] = isDarkMode
    ? (theme.colors.topbarBorderDark || darkenColor(topbarBorder, 25))
    : topbarBorder;

  // Login
  const loginColor = (isDarkMode ? theme.background.loginColorDark : theme.background.loginColor) || theme.background.loginColor || '';
  vars['--login-bg'] = loginColor || vars['--sidebar'];
  vars['--login-bg-image'] = theme.background.loginImage
    ? `url(${theme.background.loginImage})`
    : (theme.background.image ? `url(${theme.background.image})` : 'none');
  vars['--bg-image'] = theme.background.image ? `url(${theme.background.image})` : 'none';

  // Footer
  const footerBg = theme.colors.footerBackground || '#ffffff';
  const footerBorder = theme.colors.footerBorder || '#e5e7eb';
  const footerText = theme.colors.footerText || '#6b7280';
  vars['--footer'] = isDarkMode
    ? (theme.colors.footerBackgroundDark || darkenColor(footerBg, 85))
    : footerBg;
  vars['--footer-foreground'] = isDarkMode
    ? (theme.colors.footerTextDark || lightenColor(footerText, 50))
    : footerText;
  vars['--footer-border'] = isDarkMode
    ? (theme.colors.footerBorderDark || darkenColor(footerBorder, 70))
    : footerBorder;

  // Menus (sidebar navigation)
  Object.assign(vars, generateSidebarMenuVariables('sidebar-menu', theme.menus.main, isDarkMode));
  Object.assign(vars, generateSidebarMenuVariables('sidebar-submenu', theme.menus.submenu, isDarkMode));

  // Brand colors (block1 = primary, block2 = accent)
  const block1Bg = theme.blocks.block1.background || '#18344f';
  const block1Text = theme.blocks.block1.text || '#ffffff';
  const block2Bg = theme.blocks.block2.background || '#e35b00';
  const block2Text = theme.blocks.block2.text || '#ffffff';

  vars['--brand-primary'] = isDarkMode ? darkenColor(block1Bg, 20) : block1Bg;
  vars['--brand-primary-foreground'] = block1Text;
  vars['--brand-primary-hover'] = isDarkMode ? darkenColor(block1Bg, 10) : lightenColor(block1Bg, 10);

  vars['--brand-accent'] = isDarkMode ? darkenColor(block2Bg, 15) : block2Bg;
  vars['--brand-accent-foreground'] = block2Text;
  vars['--brand-accent-hover'] = isDarkMode ? darkenColor(block2Bg, 5) : lightenColor(block2Bg, 10);

  // Themed table (DataGrid)
  vars['--brand-table-header'] = isDarkMode ? darkenColor(block1Bg, 15) : block1Bg;
  vars['--brand-table-header-foreground'] = block1Text;
  vars['--brand-table-accent'] = isDarkMode ? darkenColor(block2Bg, 10) : block2Bg;
  vars['--brand-table-selected'] = isDarkMode
    ? `${block2Bg}30`
    : `${block2Bg}20`;

  // Themed card
  vars['--brand-card'] = isDarkMode ? darkenColor(block1Bg, 25) : block1Bg;
  vars['--brand-card-foreground'] = block1Text;
  vars['--brand-card-border'] = isDarkMode ? darkenColor(block1Bg, 15) : darkenColor(block1Bg, 10);

  // Logos
  vars['--logo-principal'] = theme.logos.principal ? `url(${theme.logos.principal})` : 'none';
  vars['--logo-menu'] = `url(${theme.logos.menu})`;
  vars['--logo-campanha'] = theme.logos.campanha ? `url(${theme.logos.campanha})` : 'none';

  // Button variants (tenant branding)
  const btnVariants = ['danger', 'info', 'secondary', 'success', 'warning'] as const;
  for (const name of btnVariants) {
    const v = theme.buttons[name];
    if (v) {
      vars[`--btn-${name}`] = isDarkMode ? darkenColor(v.background, 10) : v.background;
      vars[`--btn-${name}-foreground`] = v.text;
      vars[`--btn-${name}-hover`] = isDarkMode ? darkenColor(v.backgroundHover, 10) : v.backgroundHover;
    }
  }
  if (theme.buttons.primary) {
    const v = theme.buttons.primary;
    // Dark mode garante luminância mínima (55%) para contraste sobre fundo escuro,
    // mantendo a hue da marca do tenant.
    vars['--btn-primary'] = isDarkMode ? ensureMinLuminance(v.background, 55) : v.background;
    vars['--btn-primary-foreground'] = v.text;
    vars['--btn-primary-hover'] = isDarkMode ? ensureMinLuminance(v.backgroundHover, 65) : v.backgroundHover;
  }

  // Heading colors (from tenant theme)
  const h1Color = theme.colors.h1 || '#205271';
  const h2Color = theme.colors.h2 || '#205271';
  vars['--heading-h1'] = isDarkMode ? lightenColor(h1Color, 60) : h1Color;
  vars['--heading-h2'] = isDarkMode ? lightenColor(h2Color, 55) : h2Color;

  // Email link (kept for email templates)
  if (theme.colors.emailLink) vars['--email-link-color'] = theme.colors.emailLink;

  // ==========================================
  // LEGACY BRIDGE: --tmf-* aliases (backwards compatibility)
  // TODO: Remove after all consumers are migrated
  // ==========================================
  // [TMF9-54675] Em dark mode, ignora theme.background.color (que vem da config light
  // do tenant via resolvedTheme.pick) para evitar vazamento da identidade light (ex:
  // Copa verde) sobre o fundo dark. Só respeita se o tenant explicitamente definiu
  // background.colorDark.
  vars['--tmf-bg-color'] = isDarkMode
    ? (theme.background.colorDark || palette.bg)
    : (theme.background.color || '#f3f4f6');
  vars['--tmf-bg-image'] = vars['--bg-image'];
  vars['--tmf-login-bg-image'] = vars['--login-bg-image'];
  vars['--tmf-login-bg'] = vars['--login-bg'];
  vars['--tmf-text-primary'] = palette.text;
  vars['--tmf-text-secondary'] = palette.textSecondary;
  vars['--tmf-text-muted'] = palette.textMuted;
  vars['--tmf-sidebar-bg'] = vars['--sidebar'];
  vars['--tmf-topbar-bg'] = vars['--topbar'];
  vars['--tmf-topbar-text'] = vars['--topbar-foreground'];
  vars['--tmf-topbar-border'] = vars['--topbar-border'];
  vars['--tmf-footer-bg'] = vars['--footer'];
  vars['--tmf-footer-text'] = vars['--footer-foreground'];
  vars['--tmf-footer-border'] = vars['--footer-border'];
  vars['--tmf-block-primary-bg'] = vars['--brand-primary'];
  vars['--tmf-block-primary-text'] = vars['--brand-primary-foreground'];
  vars['--tmf-block-primary-hover'] = vars['--brand-primary-hover'];
  vars['--tmf-block-secondary-bg'] = vars['--brand-accent'];
  vars['--tmf-block-secondary-text'] = vars['--brand-accent-foreground'];
  vars['--tmf-block-secondary-hover'] = vars['--brand-accent-hover'];
  vars['--tmf-block-1-bg'] = vars['--brand-primary'];
  vars['--tmf-block-1-text'] = vars['--brand-primary-foreground'];
  vars['--tmf-block-2-bg'] = vars['--brand-accent'];
  vars['--tmf-block-2-text'] = vars['--brand-accent-foreground'];
  vars['--tmf-card-themed-bg'] = vars['--brand-card'];
  vars['--tmf-card-themed-text'] = vars['--brand-card-foreground'];
  vars['--tmf-card-themed-border'] = vars['--brand-card-border'];
  vars['--tmf-table-header-themed-bg'] = vars['--brand-table-header'];
  vars['--tmf-table-header-themed-text'] = vars['--brand-table-header-foreground'];
  vars['--tmf-table-accent'] = vars['--brand-table-accent'];
  vars['--tmf-table-selected-bg'] = vars['--brand-table-selected'];
  vars['--tmf-card-bg'] = palette.card;
  vars['--tmf-card-border'] = palette.border;
  vars['--tmf-card-hover'] = palette.hover;
  vars['--tmf-input-bg'] = isDarkMode ? palette.input : palette.card;
  // [TMF9-55436] --tmf-input-border usa inputBorder em ambos modos (WCAG AA).
  // Antes, light usava borderLight (#d1d5db, 1.47:1) — insuficiente.
  vars['--tmf-input-border'] = palette.inputBorder;
  vars['--tmf-input-focus-border'] = palette.focus;
  // [TMF9-55436] Placeholder usa textSecondary (mais escuro) em vez de textMuted.
  // Light: textSecondary=#4b5563 (gray-600, 7.56:1) vs textMuted=#9ca3af (gray-400, 2.54:1).
  // Dark: input bg é forçado branco → precisa de cor escura: textSecondary=#a3a3a3 dá 2.52:1
  // (fail), então usa-se inputBorder (#737373, 4.74:1) como placeholder fallback.
  vars['--tmf-input-placeholder'] = isDarkMode ? palette.inputBorder : palette.textSecondary;
  vars['--tmf-table-header-bg'] = palette.tableHeader;
  vars['--tmf-table-row-hover'] = palette.tableRowHover;
  vars['--tmf-table-border'] = palette.tableBorder;
  vars['--tmf-border-radius'] = '4px';
  vars['--tmf-border-radius-lg'] = '8px';
  vars['--tmf-border-radius-sm'] = '2px';
  vars['--tmf-shadow-sm'] = palette.shadowSm;
  vars['--tmf-shadow'] = palette.shadow;
  vars['--tmf-shadow-lg'] = palette.shadowLg;
  vars['--tmf-surface'] = palette.surface;
  vars['--tmf-elevated'] = palette.elevated;
  vars['--tmf-border'] = palette.border;
  vars['--tmf-border-light'] = palette.borderLight;
  vars['--tmf-hover'] = palette.hover;
  vars['--tmf-active'] = palette.active;
  vars['--tmf-focus'] = palette.focus;
  vars['--tmf-dialog-bg'] = palette.card;
  vars['--tmf-logo-principal'] = vars['--logo-principal'];
  vars['--tmf-logo-menu'] = vars['--logo-menu'];
  vars['--tmf-logo-campanha'] = vars['--logo-campanha'];
  const linkColor = theme.colors.link || '#205271';
  const linkHoverColor = theme.colors.linkHover || '#102a3b';
  vars['--tmf-link-color'] = isDarkMode ? lightenColor(linkColor, 50) : linkColor;
  vars['--tmf-link-hover-color'] = isDarkMode ? lightenColor(linkHoverColor, 60) : linkHoverColor;
  vars['--tmf-h1-color'] = vars['--heading-h1'];
  vars['--tmf-h2-color'] = vars['--heading-h2'];
  if (theme.colors.emailLink) vars['--tmf-email-link-color'] = theme.colors.emailLink;
  // Legacy menu vars
  const menuMain = theme.menus.main;
  const menuSub = theme.menus.submenu;
  vars['--tmf-menu-bg'] = isDarkMode ? invertForDarkMode(menuMain.background) : menuMain.background;
  vars['--tmf-menu-text'] = isDarkMode ? invertForDarkMode(menuMain.text) : menuMain.text;
  vars['--tmf-menu-border'] = menuMain.border;
  vars['--tmf-menu-active-bg'] = menuMain.activeBackground;
  vars['--tmf-menu-active-text'] = menuMain.activeText;
  vars['--tmf-menu-hover-bg'] = isDarkMode ? lightenColor(menuMain.hoverBackground, 10) : menuMain.hoverBackground;
  vars['--tmf-menu-hover-text'] = isDarkMode ? lightenColor(menuMain.hoverText, 40) : menuMain.hoverText;
  // Sidebar vars usadas pelo MenuItem
  vars['--sidebar-menu-active'] = menuMain.activeBackground;
  vars['--sidebar-menu-active-foreground'] = menuMain.activeText;
  vars['--sidebar-menu-hover'] = isDarkMode ? lightenColor(menuMain.hoverBackground, 10) : menuMain.hoverBackground;
  vars['--sidebar-menu-hover-foreground'] = isDarkMode ? lightenColor(menuMain.hoverText, 40) : menuMain.hoverText;
  vars['--tmf-submenu-bg'] = isDarkMode ? invertForDarkMode(menuSub.background) : menuSub.background;
  vars['--tmf-submenu-text'] = isDarkMode ? invertForDarkMode(menuSub.text) : menuSub.text;
  vars['--tmf-submenu-border'] = menuSub.border;
  vars['--tmf-submenu-active-bg'] = isDarkMode ? lightenColor(menuSub.activeBackground, 15) : menuSub.activeBackground;
  vars['--tmf-submenu-active-text'] = isDarkMode ? lightenColor(menuSub.activeText, 30) : menuSub.activeText;
  vars['--tmf-submenu-hover-bg'] = isDarkMode ? lightenColor(menuSub.hoverBackground, 10) : menuSub.hoverBackground;
  vars['--tmf-submenu-hover-text'] = isDarkMode ? lightenColor(menuSub.hoverText, 40) : menuSub.hoverText;
  // Legacy button vars
  for (const name of ['danger', 'info', 'secondary', 'success', 'warning'] as const) {
    const v = theme.buttons[name];
    if (v) {
      vars[`--tmf-btn-${name}-text`] = v.text;
      vars[`--tmf-btn-${name}-bg`] = isDarkMode ? darkenColor(v.background, 10) : v.background;
      vars[`--tmf-btn-${name}-bg-hover`] = isDarkMode ? darkenColor(v.backgroundHover, 10) : v.backgroundHover;
      vars[`--tmf-btn-${name}-border`] = v.background;
    }
  }
  if (theme.buttons.primary) {
    const v = theme.buttons.primary;
    // Dark mode garante luminância mínima (55%) para contraste sobre fundo escuro,
    // mantendo a hue da marca do tenant.
    const primaryBg = isDarkMode ? ensureMinLuminance(v.background, 55) : v.background;
    const primaryHover = isDarkMode ? ensureMinLuminance(v.backgroundHover, 65) : v.backgroundHover;
    vars['--tmf-btn-primary-text'] = v.text;
    vars['--tmf-btn-primary-bg'] = primaryBg;
    vars['--tmf-btn-primary-bg-hover'] = primaryHover;
    vars['--tmf-btn-primary-border'] = v.background;
    vars['--btn-primary'] = primaryBg;
    vars['--btn-primary-foreground'] = v.text;
    vars['--btn-primary-hover'] = primaryHover;
  }

  // TMF branding colors — usadas por componentes que hardcodam blue/indigo
  const tmfPrimary = theme.blocks?.block1?.background || '#18344f';
  const tmfAccent = theme.blocks?.block2?.background || '#e35b00';
  vars['--tmf-primary'] = tmfPrimary;
  vars['--tmf-accent'] = tmfAccent;

  return vars;
}

/**
 * Aplica CSS variables no documento.
 *
 * Aplica no :root todas as vars do objeto, incluindo strings vazias,
 * para garantir que vars do tenant anterior sejam sobrescritas ao chamar
 * refreshTheme() ou ao trocar de tema sem reload de página.
 *
 * Apenas `undefined` e `null` são ignorados (var sem suporte no tenant).
 *
 * @epic TMF9-56963 (Design Tokens) | @issue TMF9-56970
 */
export function applyCssVariables(variables: CssVariables): void {
  const root = document.documentElement;

  Object.entries(variables).forEach(([key, value]) => {
    if (value != null) {
      root.style.setProperty(key, value);
    }
  });
}

/**
 * Coleta os nomes das custom properties (`--x`) declaradas nas stylesheets da
 * página (index.css, theme.css, tokens.css e CSS dos componentes), descendo em
 * regras agrupadoras (`@layer`, `@media`, `@supports`). Stylesheet cross-origin
 * (sem acesso a `cssRules`) é ignorada.
 *
 * São os tokens de base do contrato (§2 de contrato-variaveis-themeable.md):
 * fixos da plataforma, nunca sobrescritos pelo payload do tenant.
 *
 * @issue TMF9-66128
 */
export function collectStylesheetCustomProperties(doc: Document = document): Set<string> {
  const names = new Set<string>();

  const visit = (rules: CSSRuleList | undefined): void => {
    if (!rules) return;
    for (const rule of Array.from(rules)) {
      const style = (rule as CSSStyleRule).style;
      if (style && typeof style.length === 'number') {
        for (let i = 0; i < style.length; i += 1) {
          const name = style.item(i);
          if (name.startsWith('--')) names.add(name);
        }
      }
      visit((rule as CSSGroupingRule).cssRules);
    }
  };

  for (const sheet of Array.from(doc.styleSheets)) {
    let rules: CSSRuleList | undefined;
    try {
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    visit(rules);
  }

  return names;
}

export interface CustomVariablesFilterResult {
  /** Vars do tenant que só ADICIONAM ao :root (fora do contrato e da base). */
  allowed: CssVariables;
  /** Chaves ignoradas por colidirem com o contrato ou com um token de base. */
  rejected: string[];
}

/**
 * Filtra `theme.customVariables` antes de aplicar no :root.
 *
 * Precedência do contrato (contrato-variaveis-themeable.md §1 e §6):
 * tokens de base (stylesheet) < vars extras do tenant < contrato gerado por
 * `generateCssVariables()`. Como `applyCssVariables()` escreve inline no :root
 * (vence qualquer stylesheet), a única forma de o tenant NÃO sobrescrever o
 * contrato nem a base é nunca escrever nessas chaves: `customVariables` só pode
 * criar variáveis que a plataforma não define.
 *
 * Antes desta função o ThemeContext aplicava `customVariables` por cima de tudo
 * (`ThemeContext.tsx:241-243`), furando a regra do §6.
 *
 * @param custom       `theme.customVariables` vindo da API.
 * @param contractVars Saída de `generateCssVariables()` para o tema/modo atual.
 * @param baseTokens   Custom properties declaradas nas stylesheets da plataforma
 *                     (ver `collectStylesheetCustomProperties()`).
 * @issue TMF9-66128
 */
export function filterCustomVariables(
  custom: CssVariables,
  contractVars: CssVariables,
  baseTokens: ReadonlySet<string> = new Set(),
): CustomVariablesFilterResult {
  const allowed: CssVariables = {};
  const rejected: string[] = [];

  Object.entries(custom).forEach(([key, value]) => {
    if (key in contractVars || baseTokens.has(key)) {
      rejected.push(key);
    } else {
      allowed[key] = value;
    }
  });

  return { allowed, rejected };
}

/**
 * Remove CSS variables do documento
 */
export function removeCssVariables(variables: CssVariables): void {
  const root = document.documentElement;

  Object.keys(variables).forEach((key) => {
    root.style.removeProperty(key);
  });
}

/**
 * Gera CSS string a partir das variáveis (útil para SSR ou export)
 */
export function generateCssString(variables: CssVariables): string {
  const cssProperties = Object.entries(variables)
    .filter(([, value]) => value)
    .map(([key, value]) => `  ${key}: ${value};`)
    .join('\n');

  return `:root {\n${cssProperties}\n}`;
}
